import React from "react";
import { CircularProgress } from "@remo-co/ui-core/src/components/CircularProgress";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Switch } from "@remo-co/ui-core/src/components/Switch";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Skeleton } from "@remo-co/ui-core/src/components/Skeleton";
import classNames from "classnames";
import { useI18n } from "i18n";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { ISsoStatus } from "./SsoConfiguration";
import { useStyles, styles } from "./styles";
import { ISsoInfo } from "./types";

interface IActivationProps {
  ssoStatus: ISsoStatus;
  isReady: boolean;
  onSyncProvider: (data: Partial<ISsoInfo>) => Promise<boolean>;
}

const Activation = ({
  ssoStatus,
  isReady,
  onSyncProvider,
}: IActivationProps) => {
  const { t } = useI18n(["common", "sso"]);
  const classes = useStyles();
  const [enable, setEnable] = React.useState(ssoStatus === ISsoStatus.ACTIVE);
  const [isLoading, setIsLoading] = React.useState(false);
  const dispatch = useAppDispatch();

  const onUpdateProviderStatus = async (partialData: Partial<ISsoInfo>) => {
    setIsLoading(true);
    await onSyncProvider(partialData);
    setIsLoading(false);
  };

  const changeStatus = (isChecked: boolean) => {
    onUpdateProviderStatus({
      enabled: isChecked,
    });
  };

  const onToggleEnable = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    // Activate directly
    if (isChecked) {
      changeStatus(isChecked);
    } else {
      // Confirm before deactivation
      dispatch(
        addDialogNotification({
          message: t("sso:disable.warning.message"),
          title: t("sso:disable.warning.title"),
          confirmBtnId: "toggle-confirm",
          dismissBtnId: "toggle-dismiss",
          onConfirm: () => changeStatus(isChecked),
          confirmText: t("disable"),
          dismissText: t("button.cancel"),
          contentStyles: styles.dialogContent,
          closeIconStyles: styles.dialogCloseIcon,
          confirmBtnStyles: styles.disableBtn,
          dismissBtnStyles: styles.cancelBtn,
        }),
      );
    }
  };

  React.useEffect(() => {
    setEnable(ssoStatus === ISsoStatus.ACTIVE);
  }, [ssoStatus]);

  return (
    <Paper
      className={classNames(classes.section, classes.spacing)}
      elevation={0}
    >
      <div
        data-testid="activation-container"
        className={classNames(classes.split, classes.splitHorizontallyCentered)}
      >
        <div>
          <Typography variant="h4">{t("sso:enable.message")}</Typography>
          {isReady ? (
            <Typography
              variant="body1"
              className={classes.avaiblityDescription}
            >
              {t("sso:availability.message")}
            </Typography>
          ) : (
            <Skeleton
              data-testid="skeleton-1"
              variant="text"
              width={350}
              height={40}
            />
          )}
        </div>

        {isReady ? (
          <FormControlLabel
            label={
              isLoading ? (
                <CircularProgress
                  size={20}
                  className={classNames("loader", classes.spinner)}
                />
              ) : (
                ""
              )
            }
            labelPlacement="start"
            data-testid="toggle-switch"
            control={
              <Switch
                color="primary"
                disabled={isLoading}
                checked={enable}
                onChange={onToggleEnable}
              />
            }
          />
        ) : (
          <Skeleton
            data-testid="skeleton-2"
            variant="rectangular"
            width={30}
            height={20}
          />
        )}
      </div>
    </Paper>
  );
};

export default Activation;
